import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Config } from '../app.configs';
import { PaginatedRecipients } from '../interfaces/paginated-recipient';
import { PaginatedCryptoRecipients } from '../interfaces/paginated-crypto-recipient.interface';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class RecipientService {
  private readonly CACHE_PREFIX_RECIPIENTS = 'recipients:';
  private readonly CACHE_PREFIX_CRYPTO_RECIPIENTS = 'crypto-recipients:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getRecipients(
    uuid: string,
    page: number = 1,
    forceRefresh: boolean = false,
  ): Observable<PaginatedRecipients> {
    const cacheKey = `${this.CACHE_PREFIX_RECIPIENTS}${uuid}:${page}`;

    return this.cacheService.get<PaginatedRecipients>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedRecipients>(
          Config.toApiUrl(`dashboard/users/${uuid}/recipients/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateRecipientsCache(uuid: string): void {
    this.cacheService.invalidateByPrefix(
      `${this.CACHE_PREFIX_RECIPIENTS}${uuid}`,
    );
  }

  createRecipient(uuid: string, recipient = {}): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(Config.toApiUrl(`dashboard/users/${uuid}/recipients/`), recipient, {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateRecipientsCache(uuid)));
  }

  getCryptoRecipients(
    uuid: string,
    page: number = 1,
    forceRefresh: boolean = false,
  ): Observable<PaginatedCryptoRecipients> {
    const cacheKey = `${this.CACHE_PREFIX_CRYPTO_RECIPIENTS}${uuid}:${page}`;

    return this.cacheService.get<PaginatedCryptoRecipients>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedCryptoRecipients>(
          Config.toApiUrl(`dashboard/users/${uuid}/crypto-recipients/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateCryptoRecipientsCache(uuid: string): void {
    this.cacheService.invalidateByPrefix(
      `${this.CACHE_PREFIX_CRYPTO_RECIPIENTS}${uuid}`,
    );
  }

  createCryptoRecipient(uuid: string, recipient = {}): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/crypto-recipients/`),
        recipient,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateCryptoRecipientsCache(uuid)));
  }
}
